import Downshift from 'downshift'
import debounce from 'lodash.debounce'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { ExclamationCircleIcon } from '@heroicons/react/solid'

import config from 'config'
import { Product } from 'models'
import { QueryFilter, ReadResult } from 'interfaces/queryOptions'
import { list as listProducts } from 'modules/Products/services/api'
import { translations } from '../../translations'

interface Props {
  onSelect: (value: ReadResult<Product>) => any
  value?: ReadResult<Product>
  error?: string | null
  filters?: QueryFilter[]
}

// Stable reference: a fresh `[]` default would change identity every render,
// re-triggering the debounced search effect below in an infinite loop
const NO_FILTERS: QueryFilter[] = []

export function ProductSearch({
  onSelect,
  error,
  value,
  filters = NO_FILTERS
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')

  const [product, setProduct] = useState<ReadResult<Product>>()
  const [products, setProducts] = useState<ReadResult<Product>[]>([])

  const onSearch = useMemo(
    () =>
      debounce(async (search: string) => {
        try {
          if (search.length < config.min_search_length) return
          setIsLoading(true)

          const result = await listProducts({
            attributes: ['id', 'name', 'code', 'type'],
            filters,
            search
          })

          setProducts(result.data)
          setIsLoading(false)
        } catch (err: any) {
          setIsLoading(false)
          console.error(err)
        }
      }, config.autocomplete_debounce_rate),
    [filters]
  )

  useEffect(() => {
    if (!value) return

    setProduct(value)
    setSearch(value.attributes.name ?? value.attributes.code ?? '')
  }, [value])

  useEffect(() => {
    if (search === '') return

    if (search === (product?.attributes.name ?? product?.attributes.code))
      return

    onSearch(search)
  }, [product, onSearch, search])

  const onClick = useCallback(
    (index: number) => {
      const selected = products[index]

      setProduct(selected)
      setSearch(selected.attributes.name ?? selected.attributes.code ?? '')
      onSelect(selected)
    },
    [products, onSelect]
  )

  return (
    <div>
      <Downshift
        onChange={(selection) => (selection ? onSelect(product!) : undefined)}
        itemToString={(item) => item?.attributes.name ?? ''}
        selectedItem={product ?? { attributes: { name: '' } }}
      >
        {({ getInputProps, getItemProps, isOpen }) => (
          <div className="relative">
            <label className="block text-left text-sm font-medium text-gray-700">
              Produto vinculado
            </label>
            <div className="relative flex items-center">
              <input
                className={
                  'mt-1 block w-full rounded-md shadow-sm py-2 px-3 border focus:outline-none ' +
                  (error
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-emerald-500')
                }
                {...getInputProps({
                  value: search || '',
                  placeholder: 'Buscar produto por nome ou código',
                  onChange: (e) => {
                    setSearch(e.currentTarget.value)
                  }
                })}
              />
              {isLoading && (
                <div className="absolute right-1">
                  <svg
                    className="animate-spin mt-1 mr-3 h-5 w-5 text-emerald-700"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
              {error && (
                <div className="absolute inset-y-0 right-0 pt-1 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon
                    className="h-5 w-5 text-red-500"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {error && (
              <p className="absolute left-0 text-xs text-red-500">{error}</p>
            )}
            <ul className="w-full absolute mt-2 rounded-md bg-white z-20 shadow flex flex-col">
              {isOpen && products.length > 0 && (
                <div className="max-h-96 overflow-auto">
                  {products.map((item, index) => (
                    <div
                      className="px-4 py-2 w-full cursor-pointer bg-white hover:bg-gray-100 flex justify-between"
                      key={item.id}
                      {...getItemProps({
                        index,
                        onClick: () => {
                          onClick(index)
                        },
                        item
                      })}
                    >
                      <div>
                        <p className="text-md text-left text-gray-900">
                          {item.attributes.name ?? item.attributes.code}
                        </p>
                        <p className="text-sm text-left text-gray-700">
                          {translations.type[item.attributes.type as string]}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isOpen && !products.length && (
                <li className="px-4 py-2 w-full cursor-pointer bg-white hover:bg-gray-100 flex justify-start">
                  Nenhum resultado encontrado
                </li>
              )}
            </ul>
          </div>
        )}
      </Downshift>
    </div>
  )
}
